import React, { useState } from "react";
import { Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// ─── Export Button ────────────────────────────────────────────────────────────
// 1. Injects a branded header div above the report root (logo + title)
// 2. Hides CTA elements (.sr-pdf-hide, .sr-footer-bar, .sr-try-another)
// 3. Captures the on-screen #screening-report-root via html2canvas
// 4. Assembles the result into a multi-page jsPDF
// 5. Restores all hidden elements and removes injected header

interface ScreeningExportPDFButtonProps {
  sessionId: string;
}

export const ScreeningExportPDFButton: React.FC<ScreeningExportPDFButtonProps> = ({
  sessionId,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    const element = document.getElementById("screening-report-root");
    if (!element) return;

    setIsExporting(true);

    // ── 1. Inject watermark overlay ───────────────────────────────────────────
    // Ant Design's Watermark renders via a canvas background-image that html2canvas
    // cannot reliably capture. We inject a positioned overlay with repeated SVG text
    // directly into the report root so it is always captured correctly.
    const watermarkSvg = encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="100">` +
        `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" ` +
        `font-family="Inter,sans-serif" font-size="18" font-weight="700" ` +
        `fill="rgba(0,0,0,0.06)" transform="rotate(-22,100,50)">QualifyMe</text>` +
      `</svg>`
    );
    const watermarkOverlay = document.createElement("div");
    watermarkOverlay.id = "sr-pdf-watermark";
    watermarkOverlay.style.cssText = [
      "position:absolute",
      "top:0",
      "left:0",
      "width:100%",
      "height:100%",
      `background-image:url("data:image/svg+xml,${watermarkSvg}")`,
      "background-repeat:repeat",
      "background-size:200px 100px",
      "pointer-events:none",
      "z-index:9999",
    ].join(";");

    // Ensure the report root is positioned so the overlay anchors correctly
    const prevPosition = element.style.position;
    if (!prevPosition || prevPosition === "static") {
      element.style.position = "relative";
    }
    element.appendChild(watermarkOverlay);

    // ── 2. Inject branded PDF header ─────────────────────────────────────────
    const pdfHeader = document.createElement("div");
    pdfHeader.id = "sr-pdf-header";
    pdfHeader.style.cssText = [
      "display:flex",
      "align-items:center",
      "justify-content:space-between",
      "background:#1a1a2e",
      "padding:16px 24px",
      "border-radius:8px 8px 0 0",
      "margin-bottom:0",
    ].join(";");

    const logoImg = document.createElement("img");
    logoImg.src = "/images/qualifyme-logo-light.png";
    logoImg.alt = "QualifyMe";
    logoImg.style.cssText = "height:32px;object-fit:contain;";

    const headerRight = document.createElement("div");
    headerRight.style.cssText = "text-align:right;";
    headerRight.innerHTML = `
      <div style="color:#fff;font-size:11px;letter-spacing:1.5px;text-transform:uppercase;font-weight:700;font-family:Inter,sans-serif;">
        QualifyMe Report
      </div>
      <div style="color:rgba(255, 255, 255, 0.75);font-size:10px;font-family:Inter,sans-serif;margin-top:2px;">
        Generated by QualifyMe AI · ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()}
      </div>
    `;

    pdfHeader.appendChild(logoImg);
    pdfHeader.appendChild(headerRight);
    element.insertBefore(pdfHeader, element.firstChild);

    // ── 3. Hide CTAs / nav elements ──────────────────────────────────────────
    const footer = document.querySelector(".sr-footer-bar") as HTMLElement | null;
    const tryAnother = document.querySelector(".sr-try-another") as HTMLElement | null;
    const pdfHideEls = Array.from(
      document.querySelectorAll(".sr-pdf-hide")
    ) as HTMLElement[];

    const prevFooter = footer?.style.display;
    const prevTryAnother = tryAnother?.style.display;
    const prevPdfHide = pdfHideEls.map((el) => el.style.display);

    if (footer) footer.style.display = "none";
    if (tryAnother) tryAnother.style.display = "none";
    pdfHideEls.forEach((el) => { el.style.display = "none"; });

    try {
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      // Use JPEG compression with quality 85 for better file size
      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      if (imgHeight <= pageHeight) {
        // Single page
        pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, imgHeight);
      } else {
        // Multi-page: slice the canvas into page-height chunks
        let yOffset = 0;
        const pageHeightInPx = (canvas.width * pageHeight) / pageWidth;

        while (yOffset < canvas.height) {
          const sliceHeight = Math.min(pageHeightInPx, canvas.height - yOffset);
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHeight;
          const ctx = sliceCanvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(canvas, 0, -yOffset);
          }
          const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.85);
          const sliceImgHeight = (sliceHeight * pageWidth) / canvas.width;

          if (yOffset > 0) pdf.addPage();
          pdf.addImage(sliceData, "JPEG", 0, 0, pageWidth, sliceImgHeight);
          yOffset += pageHeightInPx;
        }
      }

      const fileName = `screening-report-${sessionId.slice(0, 8)}-${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      pdf.save(fileName);
    } finally {
      // ── 4. Restore hidden elements ────────────────────────────────────────
      if (footer) footer.style.display = prevFooter ?? "";
      if (tryAnother) tryAnother.style.display = prevTryAnother ?? "";
      pdfHideEls.forEach((el, idx) => { el.style.display = prevPdfHide[idx] ?? ""; });

      // Remove injected header and watermark overlay
      const injected = document.getElementById("sr-pdf-header");
      if (injected) injected.remove();
      const injectedWm = document.getElementById("sr-pdf-watermark");
      if (injectedWm) injectedWm.remove();

      // Restore position
      element.style.position = prevPosition;

      setIsExporting(false);
    }
  };

  return (
    <Button
      icon={<DownloadOutlined />}
      loading={isExporting}
      disabled={isExporting}
      onClick={handleExport}
      size="large"
    >
      Export PDF
    </Button>
  );
};

export default ScreeningExportPDFButton;
